import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, MonitorSmartphone, Trash2, ShieldOff } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }); }
  catch { return iso; }
};

export function TrustedDevicesCard() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/auth/trusted-devices").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  // Only relevant when 2FA is active on the account.
  if (!user?.totp_enabled && items && items.length === 0) return null;

  const revoke = async (id) => {
    setBusy(id);
    try { await api.delete(`/auth/trusted-devices/${id}`); toast.success("Dispositivo revocato."); load(); }
    catch { toast.error("Errore nella revoca."); } finally { setBusy(""); }
  };
  const revokeAll = async () => {
    setBusy("all");
    try { await api.post("/auth/trusted-devices/revoke-all"); toast.success("Tutti i dispositivi revocati."); load(); }
    catch { toast.error("Errore nella revoca."); } finally { setBusy(""); }
  };

  return (
    <div className="card-immo p-5" data-testid="trusted-devices-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] shrink-0"><MonitorSmartphone size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Dispositivi ricordati</div>
          <p className="text-sm text-slate-500 mt-0.5">Su questi dispositivi il codice 2FA non viene richiesto per 30 giorni. Revocane l'accesso se non li riconosci.</p>
        </div>
        {items && items.length > 0 && (
          <button data-testid="trusted-revoke-all-btn" onClick={revokeAll} disabled={busy === "all"}
                  className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60">
            {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <ShieldOff size={14} />} Revoca tutti
          </button>
        )}
      </div>

      <div className="mt-4 border-t border-slate-100 pt-4">
        {items === null ? (
          <div className="flex justify-center py-4"><Loader2 className="animate-spin text-[#FC82ED]" /></div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[var(--muted)]">Nessun dispositivo ricordato.</div>
        ) : (
          <div className="grid gap-2">
            {items.map((d) => (
              <div key={d.id} data-testid={`trusted-device-${d.id}`}
                   className={`rounded-lg p-3 flex items-center gap-3 border ${d.current ? "border-[#FC82ED]/50 bg-[#FC82ED]/[0.04]" : "border-[var(--border)]"}`}>
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[var(--muted)] shrink-0"><MonitorSmartphone size={16} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">{[d.browser, d.os].filter(Boolean).join(" · ") || "Dispositivo"}</span>
                    {d.current && <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-600 font-medium">Questo dispositivo</span>}
                  </div>
                  <div className="text-xs text-[var(--muted)] mt-0.5">IP {d.ip || "—"} · ricordato dal {fmt(d.created_at)} · ultimo accesso {fmt(d.last_seen)}</div>
                  {d.days_left != null && (
                    <div data-testid={`trusted-expiry-${d.id}`} className={`text-[11px] font-medium mt-0.5 ${d.days_left <= 3 ? "text-red-600" : "text-[var(--muted)]"}`}>
                      {d.days_left === 0 ? "Scade oggi" : `Scade tra ${d.days_left} ${d.days_left === 1 ? "giorno" : "giorni"}`}
                    </div>
                  )}
                </div>
                <button data-testid={`trusted-revoke-${d.id}`} onClick={() => revoke(d.id)} disabled={busy === d.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-[var(--muted)] hover:text-red-600 hover:border-red-200 text-xs font-medium transition-colors disabled:opacity-60">
                  {busy === d.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Revoca
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
